import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Paper, CircularProgress, Alert, AlertTitle } from '@mui/material';
import { CheckCircle, Timer } from '@mui/icons-material';
import { generateQRToken, checkTokenStatus } from './services/geolocationService';
import type { TokenStatusResponse } from './services/geolocationService';

interface QRCodeDisplayProps {
  username: string;
  onSuccess: (coordinates?: TokenStatusResponse['coordinates']) => void;
  onError: (error: string) => void;
}

/**
 * Компонент для отображения QR-кода и ожидания сканирования с мобильного
 */
export const QRCodeDisplay: React.FC<QRCodeDisplayProps> = ({
  username,
  onSuccess,
  onError
}) => {
  const [qrCodeDataURL, setQrCodeDataURL] = useState<string>('');
  const [token, setToken] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(true);
  const [timeLeft, setTimeLeft] = useState(300); // 5 минут
  const [status, setStatus] = useState<'generating' | 'waiting' | 'completed' | 'expired'>('generating');

  // Генерация QR-кода
  useEffect(() => {
    const generateQR = async () => {
      try {
        setIsGenerating(true);

        // Получаем токен с сервера
        const response = await generateQRToken(username);

        if (!response.success || !response.token) {
          throw new Error('Не удалось создать токен');
        }

        const generatedToken = response.token;
        setToken(generatedToken);

        // Получаем GUID из localStorage
        const guid = localStorage.getItem('guid');

        // Создаем ссылку для QR-кода с GUID
        const qrUrl = `${window.location.origin}/mobile-geo?token=${generatedToken}${guid ? `&guid=${guid}` : ''}`;

        // Генерируем QR-код используя внешний API
        const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrUrl)}`;

        setQrCodeDataURL(qrCodeUrl);
        setStatus('waiting');
        setIsGenerating(false);

        console.log(' QR-код создан. Ожидание сканирования...');
      } catch (error) {
        console.error('Ошибка генерации QR:', error);
        onError('Не удалось создать QR-код');
        setIsGenerating(false);
      }
    };

    generateQR();
  }, [username, onError]);

  // Polling для проверки статуса токена
  useEffect(() => {
    if (!token || status !== 'waiting') return;

    const pollInterval = setInterval(async () => {
      try {
        const statusResponse: TokenStatusResponse = await checkTokenStatus(token);

        if (statusResponse.status === 'completed') {
          console.log(' Координаты получены с мобильного!');
          setStatus('completed');
          clearInterval(pollInterval);

          // Даем время показать успех, затем закрываем
          setTimeout(() => {
            onSuccess(statusResponse.coordinates);
          }, 1500);
        } else if (statusResponse.status === 'expired' || statusResponse.status === 'invalid') {
          console.warn('⚠️ Токен истек');
          setStatus('expired');
          clearInterval(pollInterval);
          onError('QR-код истек. Попробуйте еще раз');
        }
      } catch (error) {
        console.error('Ошибка проверки статуса:', error);
      }
    }, 2000); // Проверяем каждые 2 секунды

    return () => clearInterval(pollInterval);
  }, [token, status, onSuccess, onError]);

  // Таймер обратного отсчета
  useEffect(() => {
    if (status !== 'waiting') return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setStatus('expired');
          onError('Время истекло. Попробуйте еще раз');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [status, onError]);

  // Форматирование времени
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (isGenerating) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 3 }}>
        <CircularProgress />
        <Typography>Создание QR-кода...</Typography>
      </Box>
    );
  }

  if (status === 'completed') {
    return (
      <Alert severity="success" sx={{ mb: 2 }}>
        <AlertTitle> Координаты получены!</AlertTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircle />
          <Typography>Геопозиция успешно определена с мобильного устройства</Typography>
        </Box>
      </Alert>
    );
  }

  if (status === 'expired') {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        <AlertTitle>QR-код истек</AlertTitle>
        <Typography>Пожалуйста, попробуйте еще раз</Typography>
      </Alert>
    );
  }

  return (
    <Box>
      <Alert severity="warning" sx={{ mb: 3 }}>
        <AlertTitle>Требуется сканирование QR-кода</AlertTitle>
        <Typography variant="body2">
          Обнаружена низкая точность определения местоположения на этом устройстве.
          <br />
          <strong>Отсканируйте QR-код вашим смартфоном</strong> для получения точных координат через GPS.
        </Typography>
      </Alert>

      <Paper
        elevation={3}
        sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'grey.50'
        }}
      >
        {/* QR-код */}
        {qrCodeDataURL && (
          <Box
            component="img"
            src={qrCodeDataURL}
            alt="QR Code"
            sx={{
              width: 300,
              height: 300,
              border: '8px solid white',
              borderRadius: 2,
              boxShadow: 3
            }}
          />
        )}

        {/* Таймер */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Timer color="warning" />
          <Typography variant="h6" color="warning.main">
            {formatTime(timeLeft)}
          </Typography>
        </Box>

        {/* Инструкция */}
        <Box sx={{ textAlign: 'center', maxWidth: 400 }}>
          <Typography variant="body1" fontWeight="bold" gutterBottom>
            Как это работает:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            1. Откройте камеру на смартфоне
            <br />
            2. Наведите на QR-код
            <br />
            3. Перейдите по ссылке
            <br />
            4. Разрешите доступ к геолокации
            <br />
            5. Готово! Координаты автоматически передадутся
          </Typography>
        </Box>

        {/* Индикатор ожидания */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Ожидание сканирования...
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default QRCodeDisplay;

